import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { ClubsStore } from '../../../../clubs/clubs.store';
import { ToastrHelper } from '../../../../helpers/toastr.helper';
import { Member } from '../../../../models/clubs/member.model';
import { ReportModalComponent } from '../../../../shared/components/report-modal/report-modal.component';
import { CreatePublicationButtonModalComponent } from '../create-publication-modal-button/create-publication-modal-button.component';
import { UpdateCommentButtonModalComponent } from '../update-comment-modal/update-comment-modal-button.component';
import { Publication } from './../../../../models/clubs/publication.model';
import { PublicationService } from './../../../../services/clubs/publication.service';
import { AccountsService } from './../../../../services/identity/accounts.service';
import { PwaService } from './../../../../services/pwa/pwa.service';

@Component({
    selector: 'app-gear-edit-post',
    templateUrl: './gear-edit-post.component.html',
    styleUrls: ['./gear-edit-post.component.scss'],
    providers: [ConfirmationService],
})
export class GearEditPostComponent implements OnInit {
    public canRepostAsAnnouncement: boolean;
    public ceOrCette: string;
    public copyItem: MenuItem;
    @ViewChild(CreatePublicationButtonModalComponent)
    public createPublicationButtonModal: CreatePublicationButtonModalComponent;

    @ViewChild(UpdateCommentButtonModalComponent)
    public updateCommentModal: UpdateCommentButtonModalComponent;
    public deleteItem: MenuItem;
    public editPost = false;
    public isDelete = false;
    public isOnMobile = false;
    public isReportModalVisible = false;
    public isRepost = false;
    public items: MenuItem[] = [];
    public leOrLa: string;
    @Output()
    public onPublicationDeleted: EventEmitter<void> = new EventEmitter<void>();
    @Output()
    public onPublicationRepostedAsAnnouncement = new EventEmitter();
    @Output()
    public onPublicationUpdated = new EventEmitter();
    public postType: string;
    @Input() public publication: Publication;
    public publicationOwnerFullName = '';
    public reportItem: MenuItem;
    @ViewChild(ReportModalComponent)
    public reportModal: ReportModalComponent;
    public repostAsAnnouncementItem: MenuItem;
    @Input() public showEditPost: boolean;
    @Input() public showReport: boolean;
    @Input() public parentPublicationId: string;
    public updateItem: MenuItem;

    constructor(
        private accountsService: AccountsService,
        private clubsStore: ClubsStore,
        private confirmationService: ConfirmationService,
        private publicationService: PublicationService,
        private readonly pwaService: PwaService,
        private readonly toastrHelper: ToastrHelper,
        private translate: TranslateService,
    ) {}

    public addCopyItem() {
        if (this.isPublicationAPost()) {
            this.copyItem = {
                label: this.translate.instant('clubs.page.tabs.discussion.post.copyPublicationLink'),
                icon: 'icon-copy',
                command: () => {
                    const url = new URL(window.location.href);
                    url.pathname = `/clubs/${this.clubsStore.displayedRoomId}/post/${this.publication.id}`;
                    navigator.clipboard.writeText(url.href);

                    this.toastrHelper.showSuccess(this.translate.instant('clubs.page.tabs.discussion.post.copyPublicationLinkSuccess'));
                },
            };
            this.items.push(this.copyItem);
        } else {
            this.copyItem = {
                label: this.translate.instant('clubs.page.tabs.discussion.post.copyCommentLink'),
                icon: 'icon-copy',
                command: () => {
                    const url = new URL(`/clubs/${this.clubsStore.displayedRoomId}/post/${this.parentPublicationId}`, window.location.origin);
                    url.searchParams.set('comment', this.publication.id.toString());
                    navigator.clipboard.writeText(url.toString());

                    this.toastrHelper.showSuccess(this.translate.instant('clubs.page.tabs.discussion.post.copyCommentLinkSuccess'));
                },
            };
            this.items.push(this.copyItem);
        }
    }

    public confirmDelete() {
        this.isDelete = true;
        this.confirmationService.confirm({
            message: ` <span class="modal-delete-message">${this.translate.instant('clubs.page.tabs.discussion.post.editModal.title')} ${
                this.ceOrCette
            } ${this.postType} ?</span><br> ${this.translate.instant('clubs.page.tabs.discussion.post.irreversibleAction')}`,
            header: ' ', //Let Empty Please
            acceptLabel: this.translate.instant('components.button.buttonDelete'),
            acceptIcon: 'fake',
            rejectLabel: this.translate.instant('components.button.buttonCancel'),
            rejectIcon: 'fake',
            rejectButtonStyleClass: 'p-button-text',
            accept: () => {
                this.deletePost();
                this.isDelete = false;
            },
        });
    }

    public confirmRepost(publicationReposted: Publication) {
        this.isRepost = true;
        this.confirmationService.confirm({
            message: ` <span class="modal-delete-message">${this.translate.instant(
                'clubs.page.tabs.discussion.post.editModal.repostTitle',
            )} ?</span><br> ${this.translate.instant('clubs.page.tabs.discussion.post.irreversibleAction')}`,
            header: ' ', //Let Empty Please
            acceptLabel: this.translate.instant('components.button.buttonRepost'),
            acceptIcon: 'fake',
            rejectLabel: this.translate.instant('components.button.buttonCancel'),
            rejectIcon: 'fake',
            rejectButtonStyleClass: 'p-button-text',
            accept: () => {
                this.repostAsAnnouncement(publicationReposted);
                this.isRepost = false;
                this.items = this.items.filter((item) => item.icon !== 'icon-repost');
            },
        });
    }

    public defineCanRepostAsAnnoucement(): boolean {
        return this.clubsStore.isAdminOfDisplayedClub || this.clubsStore.isAnimatorOfDisplayedRoom;
    }

    public deletePost() {
        this.publicationService.deletePublication(this.publication.id).subscribe(() => {
            this.onPublicationDeleted.emit();
        });
    }

    public initiatePrimeNg() {
        if (this.accountsService.currentUser.userId == this.publication.userId) {
            this.updateItem = {
                label: `${this.translate.instant('components.button.modify')} ${this.leOrLa} ${this.postType}`,
                icon: 'icon-f-edit',
                command: () => {
                    this.editPost = true;
                    setTimeout(() => {
                        if (this.isPublicationAPost()) {
                            this.createPublicationButtonModal.openModal();
                        } else {
                            this.updateCommentModal.openModal();
                        }
                    }, 300);
                },
            };
            this.items.push(this.updateItem);
        }

        this.addCopyItem();

        this.canRepostAsAnnouncement = this.defineCanRepostAsAnnoucement();

        if (this.canRepostAsAnnouncement && !this.publication.isAnAnnouncement && this.isPublicationAPost()) {
            this.repostAsAnnouncementItem = {
                label: `${this.translate.instant('clubs.page.tabs.discussion.post.repostAsAnnouncement')}`,
                icon: 'icon-repost',
                command: () => {
                    this.confirmRepost(this.publication);
                },
            };
            this.items.push(this.repostAsAnnouncementItem);
        }
        if (this.accountsService.currentUser.userId !== this.publication.userId) {
            this.reportItem = {
                label: `${this.translate.instant('components.button.Report')} ${this.leOrLa} ${this.postType}`,
                icon: 'icon-f-flag',
                command: () => {
                    this.isReportModalVisible = true;
                    this.reportModal.openModal();
                },
                styleClass: 'menuItemReport',
            };
            this.items.push(this.reportItem);
        }

        if (
            this.accountsService.currentUser.userId == this.publication.userId ||
            this.accountsService.isAdmin() ||
            this.clubsStore.isAdminOfDisplayedClub
        ) {
            this.deleteItem = {
                label: `${this.translate.instant('components.button.buttonDelete')} ${this.leOrLa} ${this.postType}`,
                icon: 'icon-f-trash',
                command: () => {
                    this.confirmDelete();
                },
                styleClass: 'menuItemDelete',
            };

            this.items.push(this.deleteItem);
        }
    }

    public initiateWording() {
        if (this.publication.depth > 1) {
            this.postType = this.translate.instant('clubs.page.tabs.discussion.post.editModal.types.comment');
            this.leOrLa = this.translate.instant('clubs.page.tabs.discussion.post.editModal.leOrLa.le');
            this.ceOrCette = this.translate.instant('clubs.page.tabs.discussion.post.editModal.ceOrCette.ce');
        } else {
            this.postType = this.translate.instant('clubs.page.tabs.discussion.post.editModal.types.post');
            this.leOrLa = this.translate.instant('clubs.page.tabs.discussion.post.editModal.leOrLa.la');
            this.ceOrCette = this.translate.instant('clubs.page.tabs.discussion.post.editModal.ceOrCette.cette');
        }
        if (this.accountsService.currentUser.userId == this.publication.userId) {
            this.leOrLa = this.translate.instant('clubs.page.tabs.discussion.post.editModal.leOrLa.votre');
            this.ceOrCette = this.translate.instant('clubs.page.tabs.discussion.post.editModal.ceOrCette.votre');
        }
    }

    public isPublicationAPost(): boolean {
        return this.postType == this.translate.instant('clubs.page.tabs.discussion.post.editModal.types.post');
    }

    public ngOnInit() {
        this.initiateWording();
        this.initiatePrimeNg();
        this.isOnMobile = this.pwaService.isMobile;
        if (this.publication && this.publication.owner) {
            this.publicationOwnerFullName = `${this.publication.owner.firstName} ${this.publication.owner.lastName}`;
        }
    }

    public repostAsAnnouncement(publicationReposted: Publication) {
        const reposterAsAnnouncement = new Member();
        reposterAsAnnouncement.id = this.accountsService.currentUser.userId;
        reposterAsAnnouncement.lastName = this.accountsService.currentUser.lastName;
        reposterAsAnnouncement.firstName = this.accountsService.currentUser.firstName;
        reposterAsAnnouncement.pictureAccessLink = this.accountsService.currentUser.pictureReference;

        this.publication.repostedAsAnnouncement = true;
        this.publication.isAnAnnouncement = true;
        this.publication.repostAsAnnouncementTime = new Date();

        this.publication.reposterAsAnnouncement = reposterAsAnnouncement;

        this.publicationService.repostAsAnnouncement(this.publication.id).subscribe(() => {
            this.onPublicationRepostedAsAnnouncement.emit(publicationReposted);
        });
    }

    public updatePost(publicationUpdated: Publication) {
        this.publication.content = publicationUpdated.content;
        this.publication.pictureAccessLink = publicationUpdated.pictureAccessLink;
        this.publication.isAnAnnouncement = publicationUpdated.isAnAnnouncement;
        this.editPost = false;
        this.onPublicationUpdated.emit(publicationUpdated);
    }
}
